// src/pages/admin/DashboardPage.js - עם דיבוג מתקדם ובדיקת הרשאות
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase/firebaseConfig';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import adminAPI from '../../services/adminApi';
import { 
  Users, Database, TrendingUp, Activity, 
  ArrowRight, RefreshCw, AlertCircle, CheckCircle,
  Calendar, BarChart3, Settings, Eye, Shield, User
} from 'lucide-react';

// Import של AdminNavbar עם try-catch
let AdminNavbar;
try {
  AdminNavbar = require('../../components/admin/AdminNavbar').default;
 
} catch (error) {
  console.error('❌ Error importing AdminNavbar:', error);
  // Fallback navbar פשוט
  AdminNavbar = () => (
    <nav className="bg-white shadow-sm border-b border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900">ממשק ניהול - גמלנט AI</h1>
        <button 
          onClick={() => auth.signOut()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          התנתק
        </button>
      </div>
    </nav>
  );
}

const DashboardCard = ({ 
  title, 
  description, 
  icon: Icon, 
  color = "blue", 
  onClick, 
  stats = null,
  status = null 
}) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
    green: "from-green-500 to-green-600 hover:from-green-600 hover:to-green-700", 
    purple: "from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700",
    orange: "from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
      </div>
      
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      
      {stats && (
        <div className="flex items-center gap-4 text-sm">
          {stats.map((stat, index) => (
            <div key={index}>
              <span className="font-semibold text-gray-900">{stat.value}</span>
              <span className="text-gray-500 mr-1">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
      
      {status && (
        <div className="flex items-center gap-2 mt-3">
          {status.type === 'success' ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertCircle className="w-4 h-4 text-yellow-500" />
          )}
          <span className={`text-xs ${status.type === 'success' ? 'text-green-600' : 'text-yellow-600'}`}>
            {status.message}
          </span>
        </div>
      )}
    </div>
  );
};

const SystemStatusCard = ({ title, sources, onRefresh }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button 
          onClick={onRefresh}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-3">
        {sources.map((source, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${source.healthy ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <div>
                <p className="font-medium text-gray-900">{source.name}</p>
                <p className="text-xs text-gray-500">{source.records} רשומות</p>
              </div>
            </div>
            <div className="text-left">
              <p className="text-xs text-gray-500">עדכון אחרון</p>
              <p className="text-xs text-gray-700">{source.lastUpdate}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// רכיב הצגת פרטי המשתמש המחובר
const UserInfoCard = ({ user, userProfile, userType, onLogout }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            userType === 'admin' ? 'bg-red-100' : 'bg-blue-100'
          }`}>
            {userType === 'admin' ? (
              <Shield className={`w-6 h-6 ${userType === 'admin' ? 'text-red-600' : 'text-blue-600'}`} />
            ) : (
              <User className="w-6 h-6 text-blue-600" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-gray-900">
              {userProfile?.displayName || user?.displayName || 'משתמש'}
            </h3>
            <p className="text-sm text-gray-600">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2 py-1 rounded ${
                userType === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {userType === 'admin' ? 'מנהל מערכת' : 'משתמש רגיל'}
              </span>
              <span className="text-xs text-gray-500">
                UID: {user?.uid?.substring(0, 8)}...
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
        >
          התנתק
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {

  
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, isAdmin } = useAuth();
  
  const [dashboardStats, setDashboardStats] = useState({
    leads: { total: 0, today: 0, thisWeek: 0 },
    dataSources: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authCheck, setAuthCheck] = useState({
    firebaseAuth: null,
    firestoreUser: null,
    userType: null,
    hasAdminAccess: false
  });

  // פונקציה לבדיקת הרשאות מפורטת
// פונקציה לבדיקת הרשאות מפורטת - מתוקנת
const checkUserPermissions = async (currentUser) => {
 
  const authStatus = {
    firebaseAuth: null,
    firestoreUser: null,
    userType: null,
    hasAdminAccess: false
  };

  try {
    // בדיקה 1: Firebase Auth
  
    if (currentUser) {
      authStatus.firebaseAuth = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName
      };
    
    
    } else {
      console.log('   ❌ Firebase Auth: לא מחובר');
      return authStatus;
    }

  

    if (userProfile) {
      authStatus.firestoreUser = userProfile;
   
    } else {
      console.log('   ⚠️ Firestore Profile: לא קיים, מנסה לטעון ידנית...');
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          authStatus.firestoreUser = userData;
         
        } else {
          console.log('   ❌ Firestore Profile: לא נמצא');
        }
      } catch (firestoreError) {
        console.log('   ❌ Firestore Profile: שגיאה בטעינה', firestoreError);
      }
    }


    
    const isFirebaseOnlyAdmin = currentUser && !authStatus.firestoreUser;
    const isFirestoreAdmin = authStatus.firestoreUser?.isAdmin === true;
    
    // 🔧 תיקון: קריאה נכונה לפונקציה וביצוע בדיקה מפורשת
    let isContextAdmin = false;
    try {
      const adminResult = typeof isAdmin === 'function' ? isAdmin() : isAdmin;
      isContextAdmin = adminResult === true;
    
    } catch (error) {
     
      isContextAdmin = false;
    }
    
   
    // 🔧 תיקון: בדיקה מפורשת לכל תנאי
    if (isFirebaseOnlyAdmin) {
      authStatus.userType = 'firebase-admin';
      authStatus.hasAdminAccess = true;
  
    } else if (isFirestoreAdmin === true) {
      authStatus.userType = 'firestore-admin';
      authStatus.hasAdminAccess = true;
    
    } else if (isContextAdmin === true) {
      authStatus.userType = 'context-admin';
      authStatus.hasAdminAccess = true;
      
    } else if (authStatus.firestoreUser && authStatus.firestoreUser.isAdmin === false) {
      authStatus.userType = 'regular-user';
      authStatus.hasAdminAccess = false;
    
    } else {
      authStatus.userType = 'unknown';
      authStatus.hasAdminAccess = false;
  
    }

  
    // 🔧 בדיקה נוספת: אם זה משתמש רגיל - דחה בכל מקרה
    if (authStatus.firestoreUser && authStatus.firestoreUser.isAdmin === false) {
      
      authStatus.hasAdminAccess = false;
      authStatus.userType = 'regular-user';
    }

    return authStatus;

  } catch (error) {
    console.error('❌ שגיאה בבדיקת הרשאות:', error);
    return authStatus;
  }
};

  useEffect(() => {
   
    // בדיקת אימות
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
     
      if (!currentUser) {
    
        navigate('/admin/login');
        return;
      }

      // בדיקת הרשאות מפורטת
      const authStatus = await checkUserPermissions(currentUser);
      setAuthCheck(authStatus);

      // אם אין הרשאות אדמין - חסום גישה
      if (!authStatus.hasAdminAccess) {
       
        // הצגת הודעת שגיאה
        alert('אין לך הרשאות למערכת הניהול');
        navigate('/');
        return;
      }

     
      loadDashboardData();
    });

    return () => {
     
      unsubscribe();
    };
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
     
      setLoading(true);
      setError(null);
      
    
      const leadsSnapshot = await getDocs(collection(db, 'leads'));
      const leadsData = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt || new Date()
      }));
      
    
      
      // חישוב סטטיסטיקות לידים
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const leadsStats = {
        total: leadsData.length,
        today: leadsData.filter(lead => {
          const leadDate = new Date(lead.createdAt);
          return leadDate.toDateString() === today.toDateString();
        }).length,
        thisWeek: leadsData.filter(lead => {
          return new Date(lead.createdAt) >= weekAgo;
        }).length
      };

      let sourcesMetadata;
      
      try {
        sourcesMetadata = await adminAPI.getAllSourcesMetadata();
   
      } catch (apiError) {
        console.warn('⚠️ API error, using fallback data:', apiError);
        sourcesMetadata = {
          sources: [
            { name: 'גמל-נט', healthy: false, totalFunds: 0, lastUpdate: 'שגיאה' },
            { name: 'פנסיה-נט', healthy: false, totalFunds: 0, lastUpdate: 'שגיאה' },
            { name: 'ביטוח-נט', healthy: false, totalFunds: 0, lastUpdate: 'שגיאה' }
          ],
          totalRecords: 0,
          summary: { totalFunds: 0, activeSources: 0, categories: 0 }
        };
      }
      
      setDashboardStats({
        leads: leadsStats,
        dataSources: sourcesMetadata.sources,
        totalRecords: sourcesMetadata.totalRecords,
        summary: sourcesMetadata.summary
      });
      
      
      
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      setError(`שגיאה בטעינת נתונים: ${error.message}`);
      
      // נתוני fallback במקרה של שגיאה
      setDashboardStats({
        leads: { total: 0, today: 0, thisWeek: 0 },
        dataSources: [
          { name: 'גמל-נט', healthy: false, totalFunds: 'שגיאה', lastUpdate: 'שגיאה בטעינה' },
          { name: 'פנסיה-נט', healthy: false, totalFunds: 'שגיאה', lastUpdate: 'שגיאה בטעינה' },
          { name: 'ביטוח-נט', healthy: false, totalFunds: 'שגיאה', lastUpdate: 'שגיאה בטעינה' }
        ],
        totalRecords: 0,
        summary: { totalFunds: 0, activeSources: 0, categories: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">טוען נתונים...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">שגיאה בטעינה</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={loadDashboardData}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              נסה שוב
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* הצגת פרטי המשתמש המחובר */}
        <UserInfoCard
          user={user}
          userProfile={userProfile}
          userType={authCheck.userType}
          onLogout={handleLogout}
        />

        {/* כותרת */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">לוח בקרה ראשי</h1>
          <p className="text-gray-600">ברוכים הבאים למערכת ניהול גמלנט AI</p>
        </div>

        {/* כרטיסי ניהול ראשיים */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <DashboardCard
            title="ניהול פניות"
            description="צפייה וניהול כל הפניות מהאתר"
            icon={Users}
            color="blue"
            onClick={() => {
              navigate('/admin/leads');
            }}
            stats={[
              { value: dashboardStats.leads.total, label: 'סה״כ' },
              { value: dashboardStats.leads.today, label: 'היום' },
              { value: dashboardStats.leads.thisWeek, label: 'השבוע' }
            ]}
            status={{ 
              type: 'success', 
              message: `${dashboardStats.leads.today} פניות חדשות היום` 
            }}
          />

          <DashboardCard
            title="ניהול נתונים"
            description="צפייה ועריכת נתוני קרנות מכל המקורות"
            icon={Database}
            color="green"
            onClick={() => {
              navigate('/admin/data');
            }}
            stats={[
              { value: dashboardStats.totalRecords || '0', label: 'קרנות' },
              { value: dashboardStats.dataSources.length, label: 'מקורות' }
            ]}
            status={{ 
              type: dashboardStats.summary?.activeSources > 0 ? 'success' : 'warning', 
              message: dashboardStats.summary?.activeSources > 0 ? 'כל המקורות פעילים' : 'יש בעיות במקורות' 
            }}
          />

          <DashboardCard
            title="ניתוח ביצועים"
            description="דוחות וסטטיסטיקות מתקדמות"
            icon={TrendingUp}
            color="purple"
            onClick={() => {
              alert('ניתוח ביצועים יוטמע בגרסה הבאה');
            }}
            stats={[
              { value: '94%', label: 'זמינות' },
              { value: '2.3s', label: 'זמן טעינה' }
            ]}
            status={{ 
              type: 'success', 
              message: 'ביצועים מעולים' 
            }}
          />
        </div>

        {/* סקירת מערכת */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SystemStatusCard
            title="סטטוס מקורות נתונים"
            sources={dashboardStats.dataSources.map(source => ({
              name: source.name,
              healthy: source.healthy,
              records: source.totalFunds.toString(),
              lastUpdate: source.lastUpdate
            }))}
            onRefresh={() => {
              loadDashboardData();
            }}
          />

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">פעולות מהירות</h3>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/admin/data')}
                className="w-full flex items-center justify-between p-3 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">חפש קרן לפי מזהה</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-600" />
              </button>

              <button 
                onClick={async () => {
                  try {
                   
                    await adminAPI.refreshAllSources();
                    await loadDashboardData();
              
                  } catch (error) {
                    console.error('❌ Refresh error:', error);
                    alert('שגיאה בעדכון הנתונים');
                  }
                }}
                className="w-full flex items-center justify-between p-3 text-left bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">עדכון נתונים ידני</span>
                </div>
                <ArrowRight className="w-4 h-4 text-green-600" />
              </button>

              <button 
                onClick={() => navigate('/admin/leads')}
                className="w-full flex items-center justify-between p-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">דוח פניות שבועי</span>
                </div>
                <ArrowRight className="w-4 h-4 text-purple-600" />
              </button>
            </div>
          </div>
        </div>

        {/* סטטיסטיקות מהירות */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">סיכום מהיר</h3>
            <span className="text-xs text-gray-500">עדכון אחרון: {new Date().toLocaleTimeString('he-IL')}</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">{dashboardStats.totalRecords || 0}</div>
              <div className="text-xs text-gray-600">סה״כ קרנות במערכת</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">{dashboardStats.leads.total}</div>
              <div className="text-xs text-gray-600">פניות מלקוחות</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">
                {dashboardStats.summary?.activeSources || 0}/{dashboardStats.dataSources.length}
              </div>
              <div className="text-xs text-gray-600">מקורות פעילים</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">{dashboardStats.summary?.categories || 0}</div>
              <div className="text-xs text-gray-600">קטגוריות נתונים</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;